/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Injectable} from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class DropdownHelperService {

    /**
     * Splits a multi-select value (semicolon-separated string or array) into an array of trimmed strings
     */
    static splitMultiSelectValue(value: any): string[] {
        if (!value) {
            return [];
        }
        if (Array.isArray(value)) {
            return value.map(v => String(v).trim()).filter(v => v);
        }
        if (typeof value === 'string') {
            return value.split(/; ?/).map(v => v.trim()).filter(v => v);
        }
        return [String(value).trim()].filter(v => v);
    }

    /**
     * Finds a matching option in allowedValues by comparing using allowedValueKey
     * @param allowedValues Array of options to search
     * @param value The value to match (can be an object or a string)
     * @param allowedValueKey The key to use for comparison (e.g., 'label')
     * @returns The matching option object, or null if not found
     */
    static findOptionByAllowedValueKey(allowedValues: any[], value: any, allowedValueKey: string): any | null {
        if (!allowedValues || !allowedValueKey) {
            return null;
        }

        return allowedValues.find((option: any) => {
            // Check for object (excluding null, which typeof incorrectly reports as 'object')
            if (option == null || typeof option !== 'object') {
                return false;
            }

            const optionValue = option[allowedValueKey];
            if (optionValue === undefined) {
                return false;
            }

            // If value is an object, compare by allowedValueKey property
            if (value != null && typeof value === 'object') {
                return optionValue === value[allowedValueKey];
            }

            // If value is a string, compare with option's allowedValueKey value
            return String(optionValue).trim() === String(value).trim();
        }) || null;
    }

    /**
     * Finds a matching option in allowedValues by label (for ListManager structure)
     * @param allowedValues Array of {label, description} objects
     * @param label The label to match
     * @returns The matching option object, or null if not found
     */
    static findOptionByLabel(allowedValues: any[], label: string): any | null {
        if (!allowedValues || !label) {
            return null;
        }

        const normalizedLabel = String(label).trim();
        return allowedValues.find((option: any) => {
            // Check for object (excluding null, which typeof incorrectly reports as 'object')
            if (option != null && typeof option === 'object' && option.label !== undefined) {
                return String(option.label).trim() === normalizedLabel;
            }
            return false;
        }) || null;
    }

    /** Displays a List Manager description without changing the raw value used by editors and filters. */
    static labelWithDescription(label: any, allowedValues: any[]): string {
        const rawLabel = String(label);
        const description = this.findOptionByLabel(allowedValues, rawLabel)?.description;
        return description == null || String(description).trim() === ''
            ? rawLabel
            : `${rawLabel} (${String(description).trim()})`;
    }

    /**
     * Extracts the value from an object using allowedValueKey
     * @param obj The object to extract from
     * @param allowedValueKey The key to extract (e.g., 'label')
     * @returns The extracted value, or the original object if extraction fails
     */
    static extractValueByKey(obj: any, allowedValueKey?: string): any {
        if (!obj || !allowedValueKey) {
            return obj;
        }
        if (typeof obj === 'object' && obj[allowedValueKey] !== undefined) {
            return obj[allowedValueKey];
        }
        return obj;
    }

    /**
     * Normalizes a string value for comparison (trims and converts to string)
     */
    static normalizeString(value: any): string {
        return String(value).trim();
    }

    /**
     * Checks if a value exists in allowedValues array (for string arrays)
     * @param allowedValues Array of string values
     * @param value The value to check
     * @returns True if value exists in allowedValues
     */
    static isValueInAllowedList(allowedValues: any[], value: any): boolean {
        if (!allowedValues || allowedValues.length === 0) {
            return true; // If no allowed values, consider all valid
        }
        const normalizedValue = this.normalizeString(value);
        const normalizedAllowedValues = allowedValues.map(opt => this.normalizeString(opt));
        return normalizedAllowedValues.includes(normalizedValue);
    }
}
